import { useDisableChannel, useUpsertChannelMapping } from '@/lib/channels';
import type { ApartmentChannelMapping } from '@/lib/channels';
import { messageFromError } from '@/lib/api';
import { useToast } from '@/components/ToastProvider';
import { useConfirm } from '@/components/DialogProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

interface Props {
  apartmentID: string;
  mapping: ApartmentChannelMapping | undefined;
  canWrite: boolean;
}

export function BookingEngineCard({ apartmentID, mapping, canWrite }: Props) {
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const [err, setErr] = useState<string | null>(null);

  const disable = useDisableChannel(apartmentID);
  const upsert = useUpsertChannelMapping(apartmentID);

  const enabled = mapping?.enabled ?? false;

  async function handleEnable() {
    setErr(null);
    try {
      await upsert.mutateAsync({
        provider: 'booking_engine',
        body: {},
      });
      toast({ kind: 'success', title: 'Direct booking engine enabled' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  async function handleDisable() {
    const ok = await askConfirm({
      title: 'Disable direct booking engine?',
      body: 'Direct bookings will no longer be accepted for this apartment.',
      destructive: true,
      confirmLabel: 'Disable',
    });
    if (!ok) return;
    setErr(null);
    try {
      await disable.mutateAsync('booking_engine');
      toast({ kind: 'success', title: 'Direct booking engine disabled' });
    } catch (e) {
      setErr(messageFromError(e));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-foreground">Direct bookings</span>
        {mapping ? (
          enabled ? (
            <Badge variant="success" size="sm" dot>Enabled</Badge>
          ) : (
            <Badge variant="neutral" size="sm">Disabled</Badge>
          )
        ) : (
          <Badge variant="neutral" size="sm">Not configured</Badge>
        )}
      </div>

      <p className="text-sm text-muted">
        Enable this to allow guests to book directly through the booking engine.
        Availability is pushed to all connected OTA channels immediately on booking.
      </p>

      {canWrite && (
        <div className="flex items-center gap-2">
          {!enabled ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={upsert.isPending}
              onClick={handleEnable}
            >
              Enable
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              loading={disable.isPending}
              onClick={handleDisable}
            >
              Disable
            </Button>
          )}
        </div>
      )}

      {err && (
        <p role="alert" className="text-sm text-destructive">
          {err}
        </p>
      )}
    </div>
  );
}
